import { useQuery } from '@tanstack/react-query';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Link } from '@tanstack/react-router';
import { Temporal } from 'temporal-polyfill';
import { WEEKLY_TEACHING_HOURS_THRESHOLD_MINUTES } from '@airreading/common';

import AlarmAlertIcon from '@/components/assets/alarm-alert-icon.svg?react';
import { EntityTable } from '@/components/table/entity-table';
import { TableErrorState } from '@/components/table/table-error-state';
import { getSubSessionTeachers } from '@/lib/api-calls';
import { useErrorToast } from '@/lib/error-handling';
import { CoachOverallGradeBadge } from '@/components/coaches/coach-overall-grade-badge';
import { getCoachAttendanceGradeLabel, getOverallGradeIndex } from '@/lib/coach-utils';
import { formatWeeklyTeachingHours } from '@/lib/teaching-hours-utils';
import { BackgroundCheckStatusBadgeList } from '@/routes/_layout/teachers/-components/background-check-status-badge-list';
import { useMemo, useState } from 'react';
import { GetSubSessionTeachersResults } from '@airreading/common/data';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';

type SubSessionTeacher = GetSubSessionTeachersResults['coaches'][number];

interface SubSessionTeachersTableProps {
  sessionId: string;
  studentGroupId: string;
  startDate: Temporal.PlainDate;
  endDate: Temporal.PlainDate;
  startInstant: Temporal.Instant;
  previousTeacherIds: string[];
  onCancel: () => void;
  onSelect: (sendConfirmationEmail: boolean, teacherId: string, teacherName: string) => void;
}

export function SubSessionTeachersTable({
  studentGroupId,
  startDate,
  endDate,
  startInstant,
  previousTeacherIds,
  onCancel,
  onSelect,
}: SubSessionTeachersTableProps) {
  const [selectedTeacher, setSelectedTeacher] = useState<{ teacherId: string; teacherName: string } | null>(null);
  const [sendConfirmationEmail, setSendConfirmationEmail] = useState(true);

  const columnHelper = createColumnHelper<SubSessionTeacher>();

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        header: '',
        cell: ({ row }) => (
          <input
            type="radio"
            name="teacher-select"
            checked={selectedTeacher?.teacherId === row.original.id}
            onChange={() =>
              setSelectedTeacher({ teacherId: row.original.id, teacherName: row.original.fullNameOrEmail! })
            }
            aria-label="Select teacher"
            className="cursor-pointer accent-black"
          />
        ),
        size: 50,
      }),
      columnHelper.display({
        id: 'previouslyTaught',
        header: 'Previously taught',
        cell: ({ row }) => {
          const hasTaught = previousTeacherIds.includes(row.original.id);
          return hasTaught ? (
            <span className="text-black font-medium">✅ Yes</span>
          ) : (
            <span className="text-black">❌ No</span>
          );
        },
        size: 150,
      }),
      columnHelper.accessor('fullNameOrEmail', {
        id: 'fullNameOrEmail',
        header: 'Teacher name',
        cell: ({ row, getValue }) => {
          const weeklyTeachingMinutes = row.original.weeklyTeachingMinutes;
          const isOverTeachingHoursLimit =
            weeklyTeachingMinutes != null && weeklyTeachingMinutes >= WEEKLY_TEACHING_HOURS_THRESHOLD_MINUTES;

          return (
            <div className="flex items-center gap-1">
              <Link to={'/teachers/$teacherId'} params={{ teacherId: row.original.id }}>
                {getValue()}
              </Link>
              {isOverTeachingHoursLimit && (
                <span className="inline-flex items-center">
                  <AlarmAlertIcon aria-hidden="true" className="h-[20px] w-[20px] shrink-0 text-red-500 pb-0.5" />
                </span>
              )}
            </div>
          );
        },
      }),
      columnHelper.accessor((row) => row, {
        id: 'teacherGrade',
        header: 'Teacher grade',
        cell: ({ getValue }) => {
          const { qualityGrade } = getValue();
          return qualityGrade;
        },
      }),

      columnHelper.accessor((row) => row, {
        id: 'qualityGrade',
        header: 'Attendance grade',
        cell: ({ getValue }) => {
          const { attendanceGrade } = getValue();
          return getCoachAttendanceGradeLabel(attendanceGrade);
        },
      }),

      columnHelper.accessor('overallGrade', {
        id: 'overallGrade',
        header: 'Overall Grade',
        cell: ({ getValue }) => {
          return <CoachOverallGradeBadge grade={getValue()} />;
        },
      }),

      columnHelper.accessor('backgroundCheckStatuses', {
        id: 'backgroundCheckStatuses',
        header: 'Background Check',
        cell: ({ getValue }) => {
          const statuses = getValue();
          return statuses && statuses.length > 0 ? (
            <div className="max-w-lg">
              <BackgroundCheckStatusBadgeList statuses={statuses} />
            </div>
          ) : (
            '-'
          );
        },
      }),

      columnHelper.accessor((row) => row, {
        id: 'weeklySubCount',
        header: 'Weekly subs count',
        cell: ({ getValue }) => {
          const { weeklySubsCount } = getValue();
          return weeklySubsCount;
        },
      }),

      columnHelper.accessor('weeklyTeachingMinutes', {
        id: 'weeklyTeachingHours',
        header: 'Weekly teaching hours',
        cell: ({ getValue }) => {
          const minutes = getValue();
          return minutes == null ? '-' : formatWeeklyTeachingHours(minutes);
        },
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedTeacher],
  );

  const {
    data: coachesResp,
    isPending,
    error,
    refetch,
  } = useQuery(
    getSubSessionTeachers({
      studentGroupId,
      startDate,
      endDate,
      startInstant,
    }),
  );

  // Sort coaches by: Previously taught > Overall Grade > Weekly subs count
  const coaches = (coachesResp?.coaches || []).slice().sort((a, b) => {
    // 1) Previously taught: yes (true) first, then no (false)
    const aTaught = previousTeacherIds.includes(a.id);
    const bTaught = previousTeacherIds.includes(b.id);
    if (aTaught !== bTaught) {
      return aTaught ? -1 : 1;
    }

    // 2) Overall grade: higher first
    const aIndex = getOverallGradeIndex(a.overallGrade);
    const bIndex = getOverallGradeIndex(b.overallGrade);
    if (aIndex !== bIndex) return aIndex - bIndex;

    // 3) Weekly subs count: higher first
    return (b.weeklySubsCount || 0) - (a.weeklySubsCount || 0);
  });

  useErrorToast(error, 'Error loading available teachers. Please try again.');

  const table = useReactTable({
    data: coaches,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    rowCount: coaches.length,
    defaultColumn: {
      enableSorting: false,
    },
  });

  return (
    <>
      <EntityTable
        table={table}
        emptyState="No teachers found for this sub session"
        errorState={<TableErrorState refetchAction={refetch} />}
        hasError={!!error}
        isLoading={isPending}
        showPagination={false}
        emptyStateClassName="translate-y-0"
        hideEndMessage
      />
      <div className="flex justify-between items-center gap-2 mt-4">
        <div className="flex items-center">
          <Checkbox
            id="send-confirmation-email"
            checked={sendConfirmationEmail}
            onCheckedChange={(checked) => setSendConfirmationEmail(!!checked)}
          />
          <label htmlFor="send-confirmation-email" className="cursor-pointer pl-2">
            Send confirmation email
          </label>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              selectedTeacher &&
              onSelect?.(sendConfirmationEmail, selectedTeacher.teacherId, selectedTeacher.teacherName)
            }
            disabled={!selectedTeacher}
          >
            Select
          </Button>
        </div>
      </div>
    </>
  );
}
